import { IconArrowLeft } from 'central-icons/IconArrowLeft';
import { type ReactNode, useState } from 'react';

import { Text } from '@revelio/ui';

import {
  ALL_FILTER_DEFINITIONS,
  SUB_FILTER_IDS,
} from '../engine/filterRegistry';
import type { MultiInstanceMeta } from '../hooks/useMultiFilterEditor';
import {
  type Filter,
  FilterId,
  type FilterSelectOptions,
  FilterType,
} from '../types/filters';
import { SegmentedFilterFormRender } from './filterForms/SegmentedFilterFormRender';
import styles from './SubFilter.module.css';
import { mapSegmentKeyToLabel } from './segments/SegmentSelector';
import layoutStyles from './sub-filter.module.css';

export const getSubFilterLabelPostfix = (
  filterId: FilterId,
  primaryFilterId?: FilterId
): string | undefined => {
  if (primaryFilterId === FilterId.COMPANY && SUB_FILTER_IDS.has(filterId)) {
    return '(of entire company)';
  }
  return undefined;
};

export const getGroupFilterOptions = (
  filterId: FilterId,
  primaryFilterId?: FilterId
): FilterSelectOptions[] => {
  const filterDef = ALL_FILTER_DEFINITIONS[filterId];
  if (filterDef?.type !== FilterType.GROUP) return [];

  return (filterDef.children || [])
    .map((childId): FilterSelectOptions | null => {
      const childDef = ALL_FILTER_DEFINITIONS[childId];
      if (!childDef) return null;
      return {
        id: childId,
        name: childDef.name,
        labelPostfix: getSubFilterLabelPostfix(childId, primaryFilterId),
      };
    })
    .filter((option): option is FilterSelectOptions => option !== null);
};

interface SubFilterProps {
  filterSelectDropdown: ReactNode;
  filter: Filter | null;
  close: () => void;
  selectedMultiMeta?: MultiInstanceMeta;
}

export const SubFilter = ({
  filterSelectDropdown,
  filter,
  close,
  selectedMultiMeta,
}: SubFilterProps) => {
  const [segmentStep, setSegmentStep] = useState<number>(0);
  const [selectedSegmentKey, setSelectedSegmentKey] = useState<string | null>(
    null
  );

  const isSegmentStep1 = segmentStep === 1;
  const isSegmentStep2 = segmentStep === 2;

  const segmentLabel = mapSegmentKeyToLabel(selectedSegmentKey);

  const handleSegmentBack = () => {
    if (segmentStep === 2) {
      setSegmentStep(1);
      setSelectedSegmentKey(null);
    } else {
      setSegmentStep(0);
      setSelectedSegmentKey(null);
    }
  };

  return (
    <>
      <div className={styles.header} hidden={isSegmentStep1}>
        {isSegmentStep2 ? (
          <div className={layoutStyles.header}>
            <div className={layoutStyles.backButton}>
              <IconArrowLeft
                size="1rem"
                className={layoutStyles.backIcon}
                onClick={handleSegmentBack}
              />
            </div>
            <Text
              fontSize="x-sm"
              weight="semibold"
              className={layoutStyles.segmentLabel}
            >
              {segmentLabel?.toUpperCase()}
            </Text>
          </div>
        ) : (
          filterSelectDropdown
        )}
      </div>
      <div>
        <SegmentedFilterFormRender
          filter={filter}
          close={close}
          selectedMultiMeta={selectedMultiMeta}
          segmentStep={segmentStep}
          setSegmentStep={setSegmentStep}
          setSelectedSegmentKey={setSelectedSegmentKey}
          handleSegmentBack={handleSegmentBack}
        />
      </div>
    </>
  );
};
